export enum PointsEventType {
  correct = "correct",
  wrong = "wrong",
  hint = "hint",
  timeBonus = "timeBonus",
  event = "event",
}

export interface PointsEventData {
  points: number;
  label?: string;
  created?: number;
  type: PointsEventType;
}

/**
 * The point values a scene can be worth, as offered by the builder's scene
 * points selector. Shared so that the authoring UI, the player's scoring and
 * the server's session-score verification cannot drift apart — add a value here
 * and all three follow.
 */
export const SCENE_POINTS_OPTIONS = [0, 50, 100, 150] as const;

/** Highest value a scene can be assigned through the builder. */
export const SCENE_MAX_POINTS = Math.max(...SCENE_POINTS_OPTIONS);

/**
 * How a scene's awarded points scale against its `maxPoints`.
 *
 * Each entry is indexed by how many events of that type the scene has already
 * collected, so a list of length one can only ever be applied once (see
 * SceneModel._addPoints). That is what bounds a scene's total: the only positive
 * sources are a correct answer at 1x and `timeBonus` on top, so a scene can
 * never exceed `maxPoints * (1 + timeBonus[0])`.
 */
export const POINT_SCALES = {
  hint: [-0.2, -0.2, -0.4],
  wrong: [-0.2, -0.2, -0.4],
  timeBonus: [0.2],
  wrongSingleAttempt: [-0.8],
  wrongNoPoints: [-1],
};

const sumScales = (scales: number[]) =>
  scales.reduce((total, scale) => total + scale, 0);

/** Highest multiple of `maxPoints` a single scene can legitimately award. */
export const MAX_SCENE_POINTS_FACTOR = 1 + POINT_SCALES.timeBonus[0];

/**
 * Lowest multiple of `maxPoints` a single scene can legitimately reach.
 *
 * Hints and wrong answers are separate event families, each indexed on its own,
 * so their penalties stack — and a scene is synchronised mid-play, before the
 * correct-answer award lands. So the floor is the hint family at full penalty
 * plus the worst of the wrong-answer scales, not simply `-1`.
 */
export const MIN_SCENE_POINTS_FACTOR =
  sumScales(POINT_SCALES.hint) +
  Math.min(
    sumScales(POINT_SCALES.wrong),
    sumScales(POINT_SCALES.wrongSingleAttempt),
    sumScales(POINT_SCALES.wrongNoPoints)
  );
